// Function to chunk large text for context limits
export const chunkLargeText = (text, maxChars = 80000) => {
  if (text.length <= maxChars) return text;
  
  // Try to split at sentence boundaries first
  const sentences = text.split(/[.!?]\s+/);
  let chunks = [];
  let currentChunk = '';
  
  for (const sentence of sentences) {
    if ((currentChunk + sentence).length > maxChars && currentChunk) {
      chunks.push(currentChunk.trim());
      currentChunk = sentence;
    } else {
      currentChunk += (currentChunk ? '. ' : '') + sentence;
    }
  }
  
  if (currentChunk) chunks.push(currentChunk.trim());
  
  // If we still have chunks that are too large, split by paragraphs
  const finalChunks = [];
  for (const chunk of chunks) {
    if (chunk.length > maxChars) {
      const paragraphs = chunk.split(/\n\s*\n/);
      let currentPara = '';
      for (const para of paragraphs) {
        if ((currentPara + para).length > maxChars && currentPara) {
          finalChunks.push(currentPara.trim());
          currentPara = para;
        } else {
          currentPara += (currentPara ? '\n\n' : '') + para;
        }
      }
      if (currentPara) finalChunks.push(currentPara.trim());
    } else {
      finalChunks.push(chunk);
    }
  }
  
  // Return first chunk with indication of more content
  const firstChunk = finalChunks[0] || text.substring(0, maxChars);
  const remainingChunks = finalChunks.length - 1;
  
  if (remainingChunks > 0) {
    return firstChunk + `\n\n[Note: This document contains ${remainingChunks} additional sections. The analysis above covers the first section. The full document has been processed.]`;
  }
  
  return firstChunk;
};

// Function to extract text from files locally (like Legal AI Assistant)
export const extractTextFromFile = async (file) => {
  const fileType = file.type;
  
  try {
    if (fileType === "application/pdf") {
      try {
        // Import PDF.js dynamically
        const pdfjsLib = await import('pdfjs-dist');
        
        // Try multiple CDN sources for PDF.js worker
        const workerSources = [
          `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`,
          `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`,
          `//cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js`
        ];
        
        for (const workerSrc of workerSources) {
          try {
            pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
            break;
          } catch (e) {
            console.warn(`Failed to load PDF worker from ${workerSrc}`);
            continue;
          }
        }
        
        // Read file as array buffer
        const arrayBuffer = await file.arrayBuffer();
        
        // Load PDF
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        let fullText = '';
        
        // Extract text from each page
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          try {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            
            let pageText = '';
            if (textContent && textContent.items) {
              textContent.items.forEach((item) => {
                if (item && item.str) {
                  pageText += item.str + ' ';
                }
              });
            }
            
            if (pageText.trim()) {
              fullText += `--- Page ${pageNum} ---\n${pageText.trim()}\n\n`;
            }
          } catch (pageError) {
            console.warn(`Error extracting page ${pageNum}:`, pageError);
            fullText += `--- Page ${pageNum} ---\n[Error extracting text from this page]\n\n`;
          }
        }
        
        return fullText || `[PDF file ${file.name} processed but no readable text content found]`;
      } catch (pdfError) {
        console.error('PDF processing error:', pdfError);
        return `[PDF file ${file.name} - Error: ${pdfError.message}. Please try converting to text format.]`;
      }
      
    } else if (fileType === "text/plain") {
      // Handle plain text files
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target.result;
          if (typeof result === 'string') {
            resolve(result);
          } else {
            reject(new Error("Failed to read text file content"));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read text file"));
        reader.readAsText(file);
      });
      
    } else if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
  // DOCX text extraction using mammoth
  console.log(`📄 Processing DOCX file: ${file.name}`);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        console.log(`📄 DOCX array buffer size: ${arrayBuffer.byteLength} bytes`);
        const mammoth = await import('mammoth');
        const { value: text } = await mammoth.extractRawText({ arrayBuffer });
        console.log(`📄 DOCX extracted text length: ${text ? text.length : 0} characters`);
        console.log(`📄 DOCX first 200 chars: ${text ? text.substring(0, 200) : 'No text'}`);
        resolve(text || `[DOCX file ${file.name} processed but no readable text content found]`);
      } catch (err) {
        console.error(`📄 DOCX processing error:`, err);
        reject(new Error("DOCX processing error: " + err.message));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read DOCX file"));
    reader.readAsArrayBuffer(file);
  });
      
    } else {
      // Handle other common file types
      const fileName = file.name || 'unknown';
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || 'unknown';
      
      throw new Error(`Unsupported file type: ${fileType || 'unknown'} (.${fileExtension}). 

Supported formats:
- PDF files (.pdf)
- Text files (.txt)
- Word documents (.docx) - with limitations

Current file: ${fileName}`);
    }
  } catch (error) {
    console.error('Error extracting text from file:', error);
    throw new Error(`File processing failed for "${file.name}": ${error.message}`);
  }
};
